import type {
  CellViolationInfo,
  DetailedViolation,
  BuildCellMapOptions,
  NurseForCellMap,
} from "./types";
import {
  buildShiftIdToGroupMainLetterMap,
  buildShiftLookupMaps,
  getManpowerBucketForResolvedShift,
  resolveShiftFromScheduleCellFromMaps,
} from "@/utils/shift-util";
import { buildViolationMessage } from "./violationMessage";
import { buildViolationGroup } from "./violationGroup";
import { getViolationRange } from "./violationRange";
import {
  addViolationGroup,
  addViolationMessage,
  getOrCreateCell,
} from "./cellViolationMapHelpers";
import { buildGroupTotalsByDay } from "./manpowerTotals";

/**
 * detailed_violations → Map<"nurseId-dayIndex", CellViolationInfo>
 *
 * 모든 매핑 로직(isError, messages, groups)을 한 곳에서 pre-compute.
 * UI 컴포넌트에서는 map.get(key)로 O(1) lookup만 수행하면 됨.
 *
 * @param detailedViolations - useRosterValidation에서 생성된 검증 결과
 * @param copyNurses - 전체 간호사 목록 (스케줄 포함)
 * @param options - 시프트 코드 목록, 프리셉티 정책 등
 * @returns Map<"nurseId-dayIndex", CellViolationInfo>
 */
export function buildCellViolationMap(
  detailedViolations: DetailedViolation[],
  copyNurses: NurseForCellMap[],
  options: BuildCellMapOptions
): Map<string, CellViolationInfo> {
  const map = new Map<string, CellViolationInfo>();

  if (
    !detailedViolations ||
    detailedViolations.length === 0 ||
    !copyNurses ||
    copyNurses.length === 0
  ) {
    return map;
  }

  const {
    availableShifts,
    shiftCatalog,
    disableManpowerValidation,
    precepteePolicy,
    tailDaysCount = 0,
    manpowerValidateShiftKeys,
    includeMidInManpowerGroupMap,
    nurseLockedDaysMap,
  } = options;

  /** 1-based day → 0-based dayIndex 변환 후 잠금 여부 반환 */
  const isDayLockedForNurse = (nurseId: string, dayIndex: number): boolean => {
    if (!nurseLockedDaysMap) return false;
    const locked = nurseLockedDaysMap.get(nurseId);
    if (!locked) return false;
    return locked.has(dayIndex + 1); // lockedDays는 1-based
  };
  const tailStart = tailDaysCount; // 음수 인덱스 허용 범위: -tailStart ~ daysInMonth-1

  const catalogShifts = shiftCatalog?.shifts ?? availableShifts;
  const lookupMaps =
    shiftCatalog?.lookupMaps ?? buildShiftLookupMaps(catalogShifts);

  // 2. daysInMonth 계산
  const daysInMonth = copyNurses[0]?.schedule?.length ?? 0;
  if (daysInMonth === 0) return map;

  const manpowerAgg =
    manpowerValidateShiftKeys != null && manpowerValidateShiftKeys.length > 0
      ? {
          validateKeys: new Set(manpowerValidateShiftKeys),
          groupLetterMap:
            shiftCatalog?.shiftIdToGroupMainLetter ??
            buildShiftIdToGroupMainLetterMap(catalogShifts, {
              includeMid: includeMidInManpowerGroupMap !== false,
            }),
        }
      : undefined;

  // 3. groupTotalsByDay (manpower_shortage) — 서브 shift_id도 그룹 대표 문자로 합산
  const groupTotalsByDay = buildGroupTotalsByDay(
    copyNurses,
    lookupMaps,
    daysInMonth,
    precepteePolicy,
    manpowerAgg,
    nurseLockedDaysMap
  );

  // 4. nurse_id 인덱스 구축 (빠른 조회용)
  const nurseMap = new Map<string, NurseForCellMap>();
  copyNurses.forEach((n) => {
    const id = n.nurse_id || n.id;
    if (id) nurseMap.set(id, n);
  });

  // 5. 위반별로 영향받는 셀들에 정보 추가
  detailedViolations.forEach((v) => {
    // ── manpower_shortage/manpower_excess: 모든 간호사 × 해당 일 ──
    if (v.type === "manpower_shortage" || v.type === "manpower_excess") {
      if (disableManpowerValidation) return;
      if (v.day === undefined || v.shift === undefined) return;

      const dayIndex = v.day;

      copyNurses.forEach((nurse) => {
        const nurseId = nurse.nurse_id || nurse.id;
        if (!nurseId || !nurse.schedule) return;

        // 프리셉티 UI 제외
        if (
          precepteePolicy.excludePrecepteeFromUi &&
          nurse.preceptor?.role === "preceptee"
        ) {
          return;
        }

        // 잠금 day는 위반 표시 제외
        if (isDayLockedForNurse(nurseId, dayIndex)) return;

        const code = nurse.schedule![dayIndex];
        if (!code) return;

        // id 기반 해석: validateManpower·RosterMadeTable 통계와 동일
        const scheduleId = nurse.schedule_ids?.[dayIndex] ?? null;
        const cellResolved = resolveShiftFromScheduleCellFromMaps(
          code,
          scheduleId,
          lookupMaps
        );
        const cellBucket = manpowerAgg
          ? getManpowerBucketForResolvedShift(
              cellResolved,
              manpowerAgg.groupLetterMap,
              manpowerAgg.validateKeys
            )
          : (cellResolved?.default_shift ?? null);

        const isMatch = v.shift != null && v.shift === cellBucket;

        if (!isMatch) return;

        // groupTotal 체크 — validateManpower와 동일하게 집계된 값 사용
        const groupTotal = groupTotalsByDay.get(`${dayIndex}-${v.shift}`) || 0;
        if (v.type === "manpower_shortage" && groupTotal >= (v.required ?? 0)) {
          return;
        }
        if (
          v.type === "manpower_excess" &&
          groupTotal <= (v.required_max ?? Number.POSITIVE_INFINITY)
        ) {
          return;
        }

        const key = `${nurseId}-${dayIndex}`;
        const cell = getOrCreateCell(map, key);
        cell.isError = true;

        const msg = buildViolationMessage(
          v,
          nurse.name,
          dayIndex,
          groupTotal,
          options.formatDayLabel
        );
        addViolationMessage(cell, v, msg);

        const group = buildViolationGroup(
          v,
          dayIndex,
          groupTotal,
          v.type === "manpower_shortage" ? v.required : v.required_max,
          tailStart,
          daysInMonth
        );
        addViolationGroup(cell, group);
      });
      return;
    }

    // ── max_nig_per_month: 해당 간호사의 모든 셀 ──
    if (v.type === "max_nig_per_month") {
      if (!v.nurse_id) return;
      const nurse = nurseMap.get(v.nurse_id);
      if (!nurse) return;
      if (
        precepteePolicy.excludePrecepteeFromUi &&
        nurse.preceptor?.role === "preceptee"
      ) {
        return;
      }

      const nurseName = nurse.name || v.nurse_id;
      for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
        const key = `${v.nurse_id}-${dayIndex}`;
        const cell = getOrCreateCell(map, key);
        cell.isError = true;

        const msg = buildViolationMessage(
          v,
          nurseName,
          dayIndex,
          undefined,
          options.formatDayLabel
        );
        addViolationMessage(cell, v, msg);
        // max_nig_per_month는 시각적 그룹 바 없음
      }
      return;
    }

    // ── range-based 위반: 해당 간호사 × 날짜 범위 ──
    if (!v.nurse_id) return;
    const nurse = nurseMap.get(v.nurse_id);
    if (!nurse) return;
    if (
      precepteePolicy.excludePrecepteeFromUi &&
      nurse.preceptor?.role === "preceptee"
    ) {
      return;
    }

    const nurseName = nurse.name || v.nurse_id;
    const range = getViolationRange(v);

    // banned_day_after_eve는 day/next_day 구조
    if (v.type === "banned_day_after_eve") {
      if (v.day === undefined || v.next_day === undefined) return;
      const days = [v.day, v.next_day];
      days.forEach((dayIndex) => {
        if (dayIndex < -tailStart || dayIndex >= daysInMonth) return;
        if (v.nurse_id && isDayLockedForNurse(v.nurse_id, dayIndex)) return;
        const key = `${v.nurse_id}-${dayIndex}`;
        const cell = getOrCreateCell(map, key);

        const msg = buildViolationMessage(
          v,
          nurseName,
          dayIndex,
          undefined,
          options.formatDayLabel
        );
        addViolationMessage(cell, v, msg);

        const group = buildViolationGroup(
          v,
          dayIndex,
          undefined,
          undefined,
          tailStart,
          daysInMonth
        );
        addViolationGroup(cell, group);
      });
      return;
    }

    // range-based: max_conseq_work, three_seq_nig, two_offs_after_*
    if (!range) return;

    for (let dayIndex = range.startDay; dayIndex <= range.endDay; dayIndex++) {
      if (dayIndex < -tailStart || dayIndex >= daysInMonth) continue;
      if (v.nurse_id && isDayLockedForNurse(v.nurse_id, dayIndex)) continue;

      const key = `${v.nurse_id}-${dayIndex}`;
      const cell = getOrCreateCell(map, key);

      const msg = buildViolationMessage(
        v,
        nurseName,
        dayIndex,
        undefined,
        options.formatDayLabel
      );
      addViolationMessage(cell, v, msg);

      const group = buildViolationGroup(
        v,
        dayIndex,
        undefined,
        undefined,
        tailStart,
        daysInMonth
      );
      addViolationGroup(cell, group);
    }
  });

  return map;
}
